import {useCallback, useEffect, useMemo, useState} from 'react'
import {createFileRoute, useNavigate} from '@tanstack/react-router'
import styled, {createGlobalStyle} from 'styled-components'
import Header from '@/components/shardLibrary/Header'
import Footer from '@/components/shardLibrary/Footer'
import Collections from '@/components/shardLibrary/Collections'
import {getUserCollections, getUserShardCount} from '@/utils/api'
import type {Collection, CountResponse, ResultsResponse} from '@/types/api'
import {Container, Div} from '@/styled/common/loading'
import useResizeObserver from 'use-resize-observer/polyfilled'
import {debounce} from 'lodash'
import LoadingAnimation from '@/components/LoadingAnimation'

const GlobalStyle = createGlobalStyle`
    body {
        font-family: proxima nova !important;
    }
`

const Page = styled.div`
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0px;
    top: 0px;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    flex-direction: column;
    padding: min(30px, max(10px, min(2vw, 2vh)));
    background-color: var(--ggBackground);
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    font-family: proxima nova;

    &, * {
        box-sizing: border-box;
    }
`

const ROW_HEIGHT = 258
const COLLECTION_WIDTH = 170

const ShardLibrary = (props: any) => {
	const navigate = useNavigate()
	const [page, setPage] = useState(0)
	const [pageSize, setPageSize] = useState(10)
	const [loading, setLoading] = useState(true)
	const [collections, setCollections] = useState<ResultsResponse<Collection> | undefined>()
	const [notInCollections, setNotInCollections] = useState<CountResponse | undefined>()

	const resizeOfContent = useMemo(() =>
			debounce(({width, height}: { width?: number; height?: number }) => {
				if (!width || !height) {
					return;
				}
				const gridGap = 40
				let columns = parseInt(String(width / COLLECTION_WIDTH))
				columns = parseInt(String(width / (COLLECTION_WIDTH + (gridGap * (columns - 1) / columns))))
				let rows = 2
				if (height < (2 * ROW_HEIGHT) + gridGap) rows = 1
				setPageSize(Math.round(columns * rows) || 1)
			}, 250),
		[setPageSize])

	const {ref} = useResizeObserver({onResize: resizeOfContent})

    const totalPages = useMemo(() => collections?.count ? Math.ceil(collections.count / pageSize) : 1, [pageSize, collections])
	const collectionOffset = useMemo(() => page * pageSize, [page, pageSize])

	useEffect(() => {
		Promise.all([
			getUserCollections({
				limit: collectionOffset < 0 ? pageSize + collectionOffset : pageSize,
				offset: collectionOffset
			}),
			getUserShardCount({
				collection__id__isnull: true
			})
		])
			.then(([collectionResponse, countResponse]) => {
				setLoading(false)
				setCollections(collectionResponse.data)
				setNotInCollections(countResponse.data)
			})
	}, [collectionOffset, pageSize])

	const redirectToCollection = useCallback((id: string | number) => {
		navigate({
			to: '/shardlibrary/$collectionId',
			params: {collectionId: String(id)},
			search: {source: undefined}
		})
	}, [navigate])

	const sortCollectionsForLegacy = useMemo(() => {
		const filteredCollections: Collection[] = []
		if (collections) {
			filteredCollections.push(
				...collections.results
					.sort((a: any, b: any) =>
						a.shard_count < b.shard_count ? 1 : a.shard_count === b.shard_count ? (a.name > b.name ? 1 : -1) : -1
					)
			)
		}

		// Only show "No Collections" on last page
		if (!!notInCollections?.count && page === totalPages - 1) {
			filteredCollections.push(
				{id: 0, name: 'Not In Collection', shard_count: notInCollections.count},
			)
		}

		return {collections: filteredCollections.slice(0, pageSize)}
	}, [collections, page, pageSize, notInCollections, totalPages])

	return (
		<>
			<GlobalStyle/>
			<Page>
				{loading ? (
					<Container className={props.className || 'Loading'} style={props.style} fixed={props.fixed}>
						<LoadingAnimation/>
						{props.message && <Div>{props.message}</Div>}
					</Container>
				) : (
					<>
						<Header collectionName="Collections"/>
						<Collections
							contentRef={ref}
							collections={sortCollectionsForLegacy.collections}
							redirectToCollection={redirectToCollection}
							rowHeight={ROW_HEIGHT}
						/>
						{totalPages > 1 && <Footer totalPages={totalPages} currentPage={page} setPage={setPage}/>}
					</>
				)}
			</Page>
		</>
	)
}

export const Route = createFileRoute('/_auth/shardlibrary/')({
	component: ShardLibrary,
})
